// src/components/modals/QuickAccumulateModal.jsx

import React, { useState, useEffect, useRef } from 'react';
import { patch } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FaCog, FaSpinner, FaBolt } from 'react-icons/fa';
import useAuthStore from '../../store/authStore';
import { usePointsEngine } from '../../hooks/usePointsEngine';

const QuickAccumulateModal = ({
  ticketId,
  puntosMaximos,
  puntosAcumulados,
  onClose,
  onAccumulate,
  onOpenFullModal,
  tipoFidelizacion,
  clienteId,
  tiendaId,
  marca,
}) => {
  // Obtener información del usuario autenticado para identificar el tipo
  const { user, trabajador, franquiciado } = useAuthStore();

  // Función para obtener el símbolo de moneda
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€',
      'USD': '$',
      'PEN': 'S/',
      'CLP': '$'
    };
    return symbols[currency] || '$'; // USD como valor por defecto
  };

  const [montoConsumido, setMontoConsumido] = useState(0);
  const [selectedPoints, setSelectedPoints] = useState(1);

  // Estados para el motor de puntos automático
  const [calculatedPoints, setCalculatedPoints] = useState(0);
  const [userModifiedPoints, setUserModifiedPoints] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculatingPoints, setIsCalculatingPoints] = useState(false);
  const lastCalculatedMonto = useRef(0);

  // Hook para motor de puntos (especialmente para PUNTOS con límites)
  const ticketData = {
    puntos: puntosAcumulados,
    tipoFidelizacion: tipoFidelizacion
  };

  const {
    motorActivo: motorActivoHook,
    puntosDisponibles: puntosDisponiblesTicket,
    preview: previewCalculation,
    loading: loadingPreview,
    calcularPreview: calcularPreviewHook,
    tieneLimiteAplicado,
    puntosPerdidos: puntosPerdidosHook,
    ticketCompleto
  } = usePointsEngine(ticketData, marca, tipoFidelizacion, ticketId, []);

  // Simulación local del cálculo de puntos
  const simulatePointsCalculation = (amount, rules) => {
    const { mode, ratePerAmount, tiers, multiplierByTag, rounding } = rules;

    let points = 0;

    switch (mode) {
      case 'RATE_PER_AMOUNT':
        const { rate, step } = ratePerAmount;
        points = Math.floor(amount / step) * rate;
        break;

      case 'TIERED':
        if (tiers && tiers.length > 0) {
          const tier = tiers.find(t => amount >= t.min && (!t.max || amount <= t.max));
          if (tier) {
            points = Math.floor(amount * tier.rate);
          }
        }
        break;

      case 'MULTIPLIER_BY_TAG':
        if (multiplierByTag) {
          points = Math.floor(amount * multiplierByTag.baseRate);
        }
        break;

      default:
        points = 0;
    }

    // Aplicar redondeo
    switch (rounding) {
      case 'FLOOR':
        points = Math.floor(points);
        break;
      case 'CEIL':
        points = Math.ceil(points);
        break;
      case 'ROUND':
        points = Math.round(points);
        break;
      default:
      // NONE - no hacer nada
    }

    return Math.max(points, 0);
  };

  // Calcular puntos automáticamente cuando cambia el monto
  // EXACTAMENTE igual que AccumulatePointsModal línea 119-137
  useEffect(() => {
    // Evitar múltiples llamadas simultáneas o recalcular el mismo monto
    if (isCalculatingPoints) {
      return;
    }

    // Evitar recalcular si el monto no ha cambiado
    if (lastCalculatedMonto.current === montoConsumido && calculatedPoints > 0) {
      return;
    }

    // EXACTAMENTE igual que AccumulatePointsModal línea 120
    if (marca && marca.pointsEngineVersion === 1 && marca.pointsRules && montoConsumido > 0 && !userModifiedPoints) {
      // Para PUNTOS con motor activo, usar el hook que llama al endpoint
      if (tipoFidelizacion === 'PUNTOS' && motorActivoHook) {
        setIsCalculatingPoints(true);
        lastCalculatedMonto.current = montoConsumido;

        // EXACTAMENTE igual que AccumulatePointsModal línea 123 - pasar [] porque suma rápida no tiene productos
        calcularPreviewHook(montoConsumido, []).then((preview) => {
          if (preview) {
            const puntosCalculados = preview.points || 0;
            setCalculatedPoints(puntosCalculados);
            setSelectedPoints(puntosCalculados);
          }
        }).catch((error) => {
          console.error('Error en cálculo de puntos:', error);
          lastCalculatedMonto.current = 0; // Reset para permitir reintento
        }).finally(() => {
          setIsCalculatingPoints(false);
        });
      } else {
        // Para ACUMULACION o sin motor, usar cálculo local (EXACTAMENTE igual que línea 131-134)
        const newPoints = simulatePointsCalculation(montoConsumido, marca.pointsRules);
        setCalculatedPoints(newPoints);
        setSelectedPoints(newPoints);
        lastCalculatedMonto.current = montoConsumido;
      }
    } else if (montoConsumido === 0) {
      // Reset cuando el monto es 0
      setCalculatedPoints(0);
      setSelectedPoints(1);
      lastCalculatedMonto.current = 0;
    }
    // AccumulatePointsModal no tiene else, así que si no cumple condiciones simplemente no hace nada
  }, [montoConsumido, marca, userModifiedPoints, tipoFidelizacion, motorActivoHook]);

  const handleMontoChange = (e) => {
    let value = e.target.value.replace(',', '.');
    const numValue = Number(value);
    // Redondear a 2 decimales si el valor es válido
    if (!isNaN(numValue) && numValue >= 0) {
      setMontoConsumido(numValue);
      setUserModifiedPoints(false); // Reset cuando cambia el monto
    } else if (value === '' || value === '.') {
      // Permitir campo vacío o punto mientras se escribe
      setMontoConsumido(0);
      setUserModifiedPoints(false);
    }
  };

  const handlePointsChange = (e) => {
    const newValue = Number(e.target.value);

    // Para PUNTOS con motor activo, validar límite
    if (tipoFidelizacion === 'PUNTOS' && marca?.pointsEngineVersion === 1 && puntosDisponiblesTicket !== Infinity) {
      if (newValue > puntosDisponiblesTicket) {
        Swal.fire({
          title: 'Límite Excedido',
          text: `No puedes acumular más de ${puntosDisponiblesTicket} puntos. El ticket tiene un límite de ${puntosMaximos} puntos.`,
          icon: 'warning',
          confirmButtonText: 'Aceptar'
        });
        setSelectedPoints(puntosDisponiblesTicket);
        return;
      }
    }

    setSelectedPoints(newValue);

    // Si el usuario modifica manualmente, marcar como modificado
    if (marca && marca.pointsEngineVersion === 1 && marca.pointsRules) {
      setUserModifiedPoints(true);
    }
  };

  const handleAccumulate = async () => {
    // Validar si el ticket está completo (solo para PUNTOS)
    if (tipoFidelizacion === 'PUNTOS' && ticketCompleto()) {
      Swal.fire({
        title: 'Ticket Completo',
        text: 'Este ticket ya ha alcanzado el máximo de puntos.',
        icon: 'warning',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    // Determinar puntos a usar (calculados automáticamente o modificados por usuario)
    let pointsToUse = (marca && marca.pointsEngineVersion === 1 && marca.pointsRules && !userModifiedPoints)
      ? calculatedPoints
      : selectedPoints;

    // Para PUNTOS con motor activo, validar límites antes de acumular
    if (tipoFidelizacion === 'PUNTOS' && marca?.pointsEngineVersion === 1 && puntosDisponiblesTicket !== Infinity) {
      // Verificar si se perderán puntos
      const puntosPerdidos = puntosPerdidosHook();

      if (puntosPerdidos > 0) {
        const result = await Swal.fire({
          title: 'Advertencia',
          html: `Se perderán <strong>${puntosPerdidos}</strong> puntos por superar el límite del ticket.<br>¿Deseas continuar?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, continuar',
          cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      // Ajustar puntos según límite disponible
      pointsToUse = Math.min(pointsToUse, puntosDisponiblesTicket);
    }

    setIsSubmitting(true);

    try {
      // Determinar el tipo de usuario que está acumulando puntos
      const userType = franquiciado ? 'franquiciado' : trabajador ? 'trabajador' : 'propietario';
      const userId = franquiciado?.franquiciadoId || trabajador?.trabajadorId || user?.userId;

      // Acumular puntos directamente (sin productos, descuentos u ofertas)
      const ticketActualizado = await patch(`tickets/${ticketId}/accumulate`, {
        ticketId,
        monto: montoConsumido,
        puntos: pointsToUse,
        productos: [], // Sin productos en suma rápida
        tiendaId,
        userType,
        userId,
      });

      // Obtener calcMeta de la respuesta si está disponible
      let calcMeta = null;
      if (ticketActualizado && ticketActualizado.historialConsumiciones && ticketActualizado.historialConsumiciones.length > 0) {
        const ultimaConsumicion = ticketActualizado.historialConsumiciones[ticketActualizado.historialConsumiciones.length - 1];
        calcMeta = ultimaConsumicion?.calcMeta || null;
      }

      // Notificar éxito con información detallada para PUNTOS
      if (tipoFidelizacion === 'PUNTOS' && marca?.pointsEngineVersion === 1) {
        const puntosPerdidos = calcMeta?.puntosExtrasPerdidos || puntosPerdidosHook() || 0;
        const puntosAjustados = calcMeta?.puntosAjustados || pointsToUse;
        const puntosOriginales = calcMeta?.puntosOriginales || calculatedPoints;
        const limiteSuperado = calcMeta?.limiteSuperado || (puntosPerdidos > 0);

        if (limiteSuperado && puntosPerdidos > 0) {
          Swal.fire({
            title: 'Puntos Acumulados',
            html: `
              <p>Puntos acumulados: <strong>${puntosAjustados}</strong></p>
              ${puntosOriginales !== puntosAjustados ? `<p class="text-gray-600 text-sm">Puntos calculados originalmente: ${puntosOriginales}</p>` : ''}
              <p class="text-yellow-600 mt-2">⚠️ Puntos perdidos: ${puntosPerdidos}</p>
              ${puntosAjustados >= puntosDisponiblesTicket ? '<p class="text-sm text-green-600 mt-2">✅ El ticket está completo.</p>' : ''}
            `,
            icon: 'success',
            confirmButtonText: 'Aceptar'
          });
        } else {
          Swal.fire({
            title: 'Éxito',
            text: `Puntos calculados automáticamente: ${puntosAjustados} puntos acumulados.`,
            icon: 'success',
            confirmButtonText: 'Aceptar'
          });
        }
      } else {
        // Mensaje estándar para ACUMULACION o sin motor
        const successMessage = (marca && marca.pointsEngineVersion === 1 && marca.pointsRules && !userModifiedPoints)
          ? `Puntos calculados automáticamente: ${calculatedPoints} puntos acumulados.`
          : userModifiedPoints
            ? `Puntos modificados manualmente: ${selectedPoints} puntos acumulados.`
            : "Puntos acumulados correctamente.";

        Swal.fire("Éxito", successMessage, "success");
      }

      if (onAccumulate) {
        onAccumulate(pointsToUse, montoConsumido);
      }

      onClose();
    } catch (error) {
      console.error("Error al acumular puntos:", error);
      Swal.fire("Error", "No se pudieron procesar los puntos.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currencySymbol = getCurrencySymbol(marca?.pointsRules?.currency || 'EUR');
  const tieneMotor = marca?.pointsEngineVersion === 1 && marca?.pointsRules;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto flex items-center justify-center p-2 sm:p-4">
      {/* Fondo oscuro */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose}></div>

      {/* Contenido del modal */}
      <div className="bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-md max-h-[95vh] flex flex-col relative z-20">
        {/* Header del modal */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 py-4 sm:py-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaBolt className="text-yellow-300 mr-2 text-xl" />
              <h2 className="text-lg sm:text-xl font-bold text-white">Suma Rápida</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors duration-200 p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Contenido del modal - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          {/* Monto Consumido */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              💰 Monto Consumido
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-medium">
                {currencySymbol}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={montoConsumido === 0 ? '' : montoConsumido}
                onChange={handleMontoChange}
                onBlur={(e) => {
                  // Asegurar que siempre tenga máximo 2 decimales al perder el foco
                  const numValue = Number(e.target.value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    const roundedValue = Math.round(numValue * 100) / 100;
                    setMontoConsumido(roundedValue);
                  } else {
                    setMontoConsumido(0);
                  }
                }}
                className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                placeholder="0.00"
                autoFocus
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Máximo 2 decimales
            </p>
          </div>

          {/* Separador */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Motor de Puntos Automático - Solo si está configurado */}
          {tieneMotor && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <FaCog className="mr-2 text-blue-600" />
                  <h4 className="text-sm font-medium text-blue-800">⚙️ Cálculo Automático</h4>
                </div>

                <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Puntos calculados:</span>
                    <div className="flex items-center space-x-2">
                      {(isCalculatingPoints || loadingPreview) ? (
                        <FaSpinner className="w-4 h-4 animate-spin text-blue-500" />
                      ) : (
                        <span className="text-lg font-bold text-blue-600">
                          {calculatedPoints} puntos
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Basado en {marca.pointsRules.mode === 'RATE_PER_AMOUNT'
                      ? `${marca.pointsRules.ratePerAmount?.rate || 1} punto(s) por cada ${marca.pointsRules.ratePerAmount?.step || 1}${currencySymbol}`
                      : marca.pointsRules.mode === 'TIERED'
                        ? 'sistema por niveles'
                        : 'multiplicadores por categoría'
                    }
                  </p>
                </div>

                {/* Información de límites para PUNTOS */}
                {tipoFidelizacion === 'PUNTOS' && puntosDisponiblesTicket !== Infinity && (
                  <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Puntos disponibles:</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {puntosDisponiblesTicket} / {puntosMaximos}
                      </span>
                    </div>
                  </div>
                )}

                {/* Advertencia si se perderán puntos */}
                {tipoFidelizacion === 'PUNTOS' && tieneLimiteAplicado() && puntosPerdidosHook() > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    <div className="flex items-start">
                      <span className="text-yellow-600 mr-2">⚠️</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">
                          Se ajustarán a {puntosDisponiblesTicket} puntos (límite del ticket)
                        </p>
                        <p className="text-xs text-yellow-600 mt-1">
                          Se perderán {puntosPerdidosHook()} puntos por superar el límite
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-xs text-blue-600 mb-3">
                  💡 Puedes modificar el valor calculado si lo deseas
                </p>
              </div>
            </div>
          )}

          {/* Puntos a Acumular */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Puntos a Acumular</h3>

            {/* Input de puntos */}
            {tipoFidelizacion === 'PUNTOS' ? (
              tieneMotor ? (
                // Para PUNTOS con motor activo, mostrar input numérico con límite
                <div>
                  <input
                    type="number"
                    value={selectedPoints}
                    onChange={handlePointsChange}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                    min="1"
                    max={puntosDisponiblesTicket !== Infinity ? puntosDisponiblesTicket : undefined}
                  />
                  {puntosDisponiblesTicket !== Infinity && (
                    <p className="text-xs text-gray-500 mt-1">
                      Máximo: {puntosDisponiblesTicket} puntos disponibles de {puntosMaximos} totales
                    </p>
                  )}
                </div>
              ) : (
                // Para PUNTOS sin motor, usar input numérico básico
                <input
                  type="number"
                  value={selectedPoints}
                  onChange={handlePointsChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                  min="1"
                  max={puntosMaximos - puntosAcumulados}
                />
              )
            ) : (
              <input
                type="number"
                value={selectedPoints}
                onChange={handlePointsChange}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                min="1"
              />
            )}

            {/* Indicador si el usuario modificó los puntos */}
            {tieneMotor && userModifiedPoints && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-700">
                  ⚠️ Has modificado el valor calculado automáticamente
                </p>
              </div>
            )}
          </div>

          {/* Mensaje si no hay motor */}
          {!tieneMotor && (
            <div className="mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 text-center">
                  💡 Configura el motor de puntos para ver el cálculo automático
                </p>
                {onOpenFullModal && (
                  <button
                    onClick={onOpenFullModal}
                    className="mt-3 w-full text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Abrir modal completo con más opciones
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer del modal - Siempre visible */}
        <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium text-sm"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              onClick={handleAccumulate}
              disabled={
                montoConsumido <= 0 ||
                (tipoFidelizacion === 'PUNTOS' && ticketCompleto()) ||
                isSubmitting ||
                (tieneMotor && calculatedPoints <= 0 && !userModifiedPoints)
              }
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Acumulando...
                </>
              ) : (
                'Acumular Puntos'
              )}
            </button>
          </div>

          {/* Botón para abrir modal completo */}
          {onOpenFullModal && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={onOpenFullModal}
                className="w-full text-sm text-blue-600 hover:text-blue-800 underline text-center"
              >
                Más opciones (productos, descuentos, ofertas)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickAccumulateModal;
